import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { saveDeliveryRateConfiguration } from '../../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress
} from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'

const SAVE_DELIVERY_RATE_CONFIGURATION = gql`${saveDeliveryRateConfiguration}`

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }
const labelSx = { fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }

function DeliveryRate(props) {
  const [nearbyRadiusMiles, setNearbyRadiusMiles] = useState(
    props.nearbyRadiusMiles != null ? props.nearbyRadiusMiles : 6.21
  )
  // Pickup discovery (migration 139): how far the customer app looks for
  // pickup restaurants when nothing delivers to the address.
  const [pickupRadiusMiles, setPickupRadiusMiles] = useState(
    props.pickupRadiusMiles != null ? props.pickupRadiusMiles : 15
  )
  // Share of the customer's delivery tip that goes to the server (kitchen).
  // Stored on Configuration as 0–1; here we render it as a 0–100 percent.
  const [serverTipSharePct, setServerTipSharePct] = useState(
    props.serverTipShare != null ? Math.round(parseFloat(props.serverTipShare) * 100) : 10
  )
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_DELIVERY_RATE_CONFIGURATION, {
    onCompleted: () => {
      setSuccess('Delivery rate configuration saved!')
      setError('')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: () => {
      setError('Failed to save configuration.')
      setSuccess('')
      setTimeout(() => setError(''), 3000)
    }
  })

  const handleSave = () => {
    const radius = Number(nearbyRadiusMiles)
    if (Number.isNaN(radius) || radius <= 0 || radius > 100) {
      setError('Discovery radius must be between 0 and 100 miles')
      setTimeout(() => setError(''), 3000)
      return
    }
    const pickupRadius = Number(pickupRadiusMiles)
    if (Number.isNaN(pickupRadius) || pickupRadius < 1 || pickupRadius > 100) {
      setError('Pickup radius must be between 1 and 100 miles')
      setTimeout(() => setError(''), 3000)
      return
    }
    const sharePct = Number(serverTipSharePct)
    if (Number.isNaN(sharePct) || sharePct < 0 || sharePct > 100) {
      setError('Server tip share must be between 0 and 100')
      setTimeout(() => setError(''), 3000)
      return
    }
    mutate({
      variables: {
        configurationInput: {
          nearbyRadiusMiles: radius,
          pickupRadiusMiles: pickupRadius,
          serverTipShare: sharePct / 100,
        },
      },
    })
  }

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a2f 60%, #065f46 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LocalShippingIcon sx={{ color: '#6ee7b7', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Delivery Rate</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Discovery Radius (miles)</Typography>
          <TextField
            fullWidth size="small" type="number"
            inputProps={{ step: 0.5, min: 0.5, max: 100 }}
            placeholder="e.g. 6.21"
            value={nearbyRadiusMiles}
            onChange={e => setNearbyRadiusMiles(e.target.value)}
            helperText="How far (straight-line) customers can see restaurants from their delivery address. Per-restaurant delivery zones still apply on top of this."
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Pickup Radius (miles)</Typography>
          <TextField
            fullWidth size="small" type="number"
            inputProps={{ step: 1, min: 1, max: 100 }}
            placeholder="e.g. 15"
            value={pickupRadiusMiles}
            onChange={e => setPickupRadiusMiles(e.target.value)}
            helperText="When no restaurant delivers to a customer's address, the app lists restaurants offering pickup within this distance (straight-line), nearest first."
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Server Tip Share (%)</Typography>
          <TextField
            fullWidth size="small" type="number"
            inputProps={{ step: 1, min: 0, max: 100 }}
            placeholder="e.g. 10"
            value={serverTipSharePct}
            onChange={e => setServerTipSharePct(e.target.value)}
            helperText="On delivery orders the customer leaves one tip. This % goes to the server (kitchen); the rest goes to the driver."
            sx={fieldSx}
          />
        </Box>

        {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSave}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#065f46', '&:hover': { bgcolor: '#064e3b' } }}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(DeliveryRate)